/**
 * OnboardingWalkthrough — one-time guided setup sequence for vendor / partner
 * services, shown on a student's first dashboard visit. Deliberately honest UX:
 *
 *   - "Skip this step" carries equal visual weight to "Next".
 *   - No urgency copy, no timers, no "recommended" pushiness on any category.
 *   - Backdrop click and Esc close the modal without penalty; the same close
 *     path calls the same "mark seen" endpoint used by Finish.
 *   - Dashboard renders a permanent "Review setup options" link (see
 *     Dashboard.jsx) so anyone who skipped can re-open the same content later —
 *     re-opening does NOT unset the seen flag, it just shows the modal again.
 *
 * iter61 — Portaled to document.body via createPortal. Fixes a real bug where
 * the modal was rendering inline within the Dashboard's `<main class="moe-fade-up">`
 * (which has `transform: matrix(...)` applied by the entrance animation). Per
 * CSS spec, any `transform` on an ancestor makes `position: fixed` behave as
 * `position: absolute` — meaning the modal was positioning relative to <main>
 * (height 4278px, spanning the entire document) instead of the viewport (1080px).
 * That caused the reported symptoms: modal card off-screen, backdrop covering
 * the whole document flow, dashboard content bleeding through underneath.
 * Portaling to document.body sidesteps the transformed ancestor entirely.
 *
 * Data source: /api/countries/{country}/vendor-links. Category order comes
 * from the backend (vendor_links_meta.category_order). We wrap each category
 * as one step; the "restricted items" / "buy after arrival" style category
 * grouping is intentional — timeline stage is implicit in the backend's ordering.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { X, ChevronLeft, ChevronRight, ExternalLink, Info } from "lucide-react";

export default function OnboardingWalkthrough({ open, onClose, country, hostCity, caseId }) {
  const [vendor, setVendor] = useState(null);
  const [idx, setIdx] = useState(0);
  const [loadError, setLoadError] = useState(false);
  // Tracks whether the user reached the end via the Finish button (Next on last
  // step). Any other close path — X, Esc, backdrop, Skip button, "Skip and
  // close" — leaves this false and is reported to the backend as "skipped".
  // The backend uses this to decide whether to render the quiet nudge line on
  // the dashboard afterwards. See server.py::mark_onboarding_seen and
  // Dashboard.jsx (skipped-outcome nudge).
  const completedRef = useRef(false);

  useEffect(() => {
    if (!open || !country) return;
    setIdx(0);
    setLoadError(false);
    completedRef.current = false;
    api.get(`/countries/${encodeURIComponent(country)}/vendor-links`)
      .then((r) => setVendor(r.data))
      .catch(() => setLoadError(true));
  }, [open, country]);

  // Mark the case's walkthrough as seen — awaited inside `close()` so the
  // Dashboard's onClose→load() sees the persisted outcome without a reload.
  // Silent-fail on network error: a failure here should not block the user
  // from closing the modal, but we log to console so a genuine 5xx is at
  // least visible in DevTools during triage.
  const markSeen = useCallback(async (outcome) => {
    try {
      await api.post("/cases/me/onboarding-seen", { outcome });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[onboarding] failed to persist outcome", e);
    }
  }, []);

  const close = useCallback(async () => {
    const outcome = completedRef.current ? "completed" : "skipped";
    await markSeen(outcome);
    onClose?.(outcome);
  }, [markSeen, onClose]);

  // Esc closes the modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  // iter61 — lock body scroll while modal is open so the underlying dashboard
  // can't be scrolled behind the backdrop. Restores previous overflow on unmount
  // so nothing leaks if the component unmounts while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const categories = vendor?.categories || [];
  const total = categories.length;
  const current = categories[idx];
  const isLast = idx >= total - 1;
  const isFirst = idx === 0;

  const goNext = () => {
    if (isLast) {
      // "Finish" click on the last step → counts as completed.
      completedRef.current = true;
      close();
    } else {
      setIdx(idx + 1);
    }
  };
  const goPrev = () => { if (!isFirst) setIdx(idx - 1); };
  // Skip is deliberately equal-weight to Next. On the last step Skip is
  // labelled "Skip and close" — it also closes the modal, but the outcome
  // stays "skipped" (completedRef untouched).
  const skipStep = () => {
    if (isLast) { close(); } else { setIdx(idx + 1); }
  };

  return createPortal(
    <div
      data-testid="onboarding-walkthrough"
      // iter62 v2 — full opacity backdrop. Previously 0.82 alpha let ~18% of
      // the dashboard bleed through (visible as colored blocks). backdrop-blur
      // support was inconsistent so it was not a reliable defense either.
      // Solid near-opaque overlay is what every serious onboarding tour uses.
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 animate-[fadeIn_150ms_ease-out]"
      style={{ backgroundColor: "rgb(10, 10, 10)" }}
      onClick={close}
    >
      <div
        className="w-full max-w-2xl bg-paper rounded-2xl shadow-2xl border border-ink/10 overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-ink/10 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Setup options</div>
            <h2 id="onboarding-title" className="mt-1 text-xl md:text-2xl font-bold tracking-tight text-ink">
              A short tour of what's available for your {country} move
            </h2>
            <p className="mt-1 text-[13px] text-muted leading-snug">
              Nothing here is required. This is a one-time overview of the vendor and partner services in scope for your case. You can skip any step and revisit this later from your dashboard.
            </p>
          </div>
          <button
            type="button"
            data-testid="onboarding-close"
            onClick={close}
            aria-label="Close"
            className="shrink-0 w-9 h-9 rounded-full hover:bg-ink/5 flex items-center justify-center text-ink/60 hover:text-ink"
          >
            <X size={18} strokeWidth={2}/>
          </button>
        </div>

        {/* Progress dots */}
        {total > 0 && (
          <div
            className="px-6 pt-4 flex items-center gap-2"
            data-testid="onboarding-progress"
          >
            {categories.map((c, i) => (
              <div
                key={c.key}
                className={`h-1 rounded-full transition-all ${i === idx ? "bg-elec flex-1" : i < idx ? "bg-ink/40 w-6" : "bg-ink/15 w-6"}`}
              />
            ))}
            <span className="ml-2 text-[11px] tabular-nums text-muted shrink-0">
              {Math.min(idx + 1, total)} of {total}
            </span>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loadError && (
            <div className="text-sm text-muted" data-testid="onboarding-error">
              Couldn't load the setup options right now. You can close this and revisit later. It's not blocking your dashboard.
            </div>
          )}
          {!loadError && !vendor && (
            <div className="space-y-3">
              <div className="moe-shimmer h-5 w-1/3"/>
              <div className="moe-shimmer h-16 w-full"/>
              <div className="moe-shimmer h-16 w-full"/>
            </div>
          )}
          {!loadError && vendor && total === 0 && (
            <div className="text-sm text-muted" data-testid="onboarding-empty">
              No vendor partners are in scope for {country} yet. You can close this. Everything you need is already on the dashboard.
            </div>
          )}
          {current && (
            <div data-testid={`onboarding-step-${current.key}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">
                Step {idx + 1} · {current.label}
              </div>
              <p className="mt-2 text-[13px] text-muted leading-relaxed">
                {hostCity
                  ? `Options in scope for ${hostCity}, ${country}. Each link opens the provider directly. You decide whether any of them fit your case.`
                  : `Options in scope for ${country}. Each link opens the provider directly. You decide whether any of them fit your case.`}
              </p>

              <div className="mt-4 space-y-2">
                {current.items.map((v) => (
                  <a
                    key={v.provider_name}
                    href={v.url}
                    target="_blank"
                    rel={v.link_type === "referral" ? "sponsored noopener" : "noopener"}
                    title={v.display_disclosure}
                    data-testid={`onboarding-vendor-${current.key}-${v.provider_name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="flex items-center justify-between gap-3 border border-ink/15 rounded-lg px-3.5 py-3 hover:bg-tint transition-colors group min-w-0 bg-white/60"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="font-bold text-ink text-sm truncate">{v.provider_name}</div>
                        <span
                          className={
                            v.link_type === "own_product"
                              ? "text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-ink text-white shrink-0"
                              : v.link_type === "free_alternative"
                                ? "text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border border-ink/30 text-ink shrink-0"
                                : "text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-elec/10 text-elec shrink-0"
                          }
                        >
                          {v.link_type === "own_product"
                            ? "Nikshala"
                            : v.link_type === "free_alternative"
                              ? "Free"
                              : "Partner"}
                        </span>
                      </div>
                      {v.note && <div className="text-[11px] text-muted truncate mt-0.5">{v.note}</div>}
                    </div>
                    <ExternalLink size={13} className="text-ink/50 group-hover:text-elec shrink-0" />
                  </a>
                ))}
              </div>

              {/* Honest disclosure inside each step so users see it regardless of which step they close on */}
              <div className="mt-4 flex items-start gap-2 text-[11px] text-muted p-2.5 rounded-lg bg-ink/[0.03]">
                <Info size={12} className="mt-0.5 shrink-0"/>
                <div>
                  Partner links help fund Ministry of Exchange. Nikshala-labelled products are shipped in-house. Neither is required for your exchange.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer. Skip and Next carry equal visual weight (same size, same font,
            same padding). Skip is styled as an outlined button, Next as a solid
            button; both use the same 40px height and 14px text so the eye doesn't
            get pulled to Next. No "Recommended" badge on either action. */}
        <div className="px-6 py-4 border-t border-ink/10 flex items-center justify-between gap-3 flex-wrap bg-white/60">
          <button
            type="button"
            data-testid="onboarding-back"
            onClick={goPrev}
            disabled={isFirst}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/70 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed h-10 px-2"
          >
            <ChevronLeft size={16}/> Back
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="onboarding-skip"
              onClick={skipStep}
              className="h-10 px-4 rounded-full border border-ink/25 bg-white text-ink text-sm font-semibold hover:bg-ink/5 transition-colors"
            >
              {isLast ? "Skip and close" : "Skip this step"}
            </button>
            <button
              type="button"
              data-testid="onboarding-next"
              onClick={goNext}
              className="h-10 px-4 rounded-full bg-ink text-white text-sm font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-1.5"
            >
              {isLast ? "Finish" : "Next"} {!isLast && <ChevronRight size={16}/>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
